// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { sortDivisions } from "@/utils/divisionSort";

interface Event {
  id: string;
  name: string;
}

interface AgeGroup {
  id: string;
  name: string;
}

interface TournamentCreationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editTournament?: Tournament | null;
  defaultEventId?: string;
}

interface Tournament {
  id: string;
  name: string;
  event_id: string;
  age_group_id: string;
  tournament_type: string;
  max_teams: number;
  seeding_type: string;
  status: string;
}

export const TournamentCreationDialog: React.FC<TournamentCreationDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editTournament,
  defaultEventId,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    event_id: "",
    age_group_id: "",
    tournament_type: "single_elimination",
    max_teams: 32,
    seeding_type: "manual",
  });
  const [events, setEvents] = useState<Event[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchEvents();
      
      // Populate form with edit data
      if (editTournament) {
        setFormData({
          name: editTournament.name,
          event_id: editTournament.event_id,
          age_group_id: editTournament.age_group_id,
          tournament_type: editTournament.tournament_type,
          max_teams: editTournament.max_teams,
          seeding_type: editTournament.seeding_type,
        });
      } else {
        // Reset form for new tournament
        setFormData({
          name: "",
          event_id: "",
          age_group_id: "",
          tournament_type: "single_elimination",
          max_teams: 32,
          seeding_type: "manual",
        });
      }
    }
  }, [isOpen, editTournament]);

  // Pre-select event when events are loaded and defaultEventId is provided
  useEffect(() => {
    if (defaultEventId && events.length > 0 && !editTournament) {
      const eventExists = events.some(e => e.id === defaultEventId);
      if (eventExists && formData.event_id !== defaultEventId) {
        console.log('[TournamentCreationDialog] Pre-selecting event from URL:', defaultEventId);
        setFormData(prev => ({ ...prev, event_id: defaultEventId }));
      }
    }
  }, [defaultEventId, events, editTournament]);

  useEffect(() => {
    if (formData.event_id) {
      fetchAgeGroups(formData.event_id);
    } else {
      setAgeGroups([]);
      setFormData(prev => ({ ...prev, age_group_id: "" }));
    }
  }, [formData.event_id]);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("events")
        .select("id, name")
        .in("status", ["published", "draft"])
        .order("name");

      if (error) throw error;
      setEvents(data || []);
    } catch (error: any) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgeGroups = async (eventId: string) => {
    try {
      // First try to fetch from new event_pricing system
      const { data: eventPricing, error: pricingError } = await supabase
        .from("event_pricing")
        .select("pricing_structure")
        .eq("event_id", eventId)
        .single();

      if (!pricingError && eventPricing?.pricing_structure?.division_pricing) {
        // Use new pricing system - create synthetic IDs for dropdown
        const divisions = eventPricing.pricing_structure.division_pricing || [];
        const mappedDivisions: AgeGroup[] = divisions.map((d: any, index: number) => ({
          id: `division-${index}-${d.name}`, // Synthetic ID including name for lookup
          name: d.name
        }));
        setAgeGroups(sortDivisions(mappedDivisions));
        return;
      }

      // Fallback to old event_age_groups table for legacy events
      const { data, error } = await supabase
        .from("event_age_groups")
        .select("id, name")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setAgeGroups(data || []);
    } catch (error: any) {
      console.error("Error fetching age groups:", error);
      toast.error("Failed to load age groups");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Tournament name is required");
      return;
    }
    
    if (!formData.event_id) {
      toast.error("Please select an event");
      return;
    }
    
    if (!formData.age_group_id) {
      toast.error("Please select an age group");
      return;
    }

    setIsSubmitting(true);
    
    console.log("🚀 Starting tournament creation/update:", { 
      isEdit: !!editTournament, 
      formData 
    });
    
    try {
      // Get the division name from the selected age group
      const selectedAgeGroup = ageGroups.find(ag => ag.id === formData.age_group_id);
      const divisionName = selectedAgeGroup?.name || '';
      
      // Check if using new pricing system (synthetic ID) or legacy age_group
      const isLegacyAgeGroup = !formData.age_group_id.startsWith('division-');
      
      if (editTournament) {
        console.log("✏️ Updating existing tournament:", editTournament.id);
        // Update existing tournament
        const { error: updateError } = await supabase
          .from("tournaments")
          .update({
            name: formData.name.trim(),
            event_id: formData.event_id,
            age_group_id: isLegacyAgeGroup ? formData.age_group_id : null,
            division_name: divisionName,
            tournament_type: formData.tournament_type,
            max_teams: formData.max_teams,
            seeding_type: formData.seeding_type,
          })
          .eq("id", editTournament.id);

        if (updateError) throw updateError;
        console.log("✅ Tournament updated successfully");
        toast.success("Tournament updated successfully");
      } else {
        console.log("🆕 Creating new tournament");
        // Create new tournament
        const { data: tournament, error: tournamentError } = await supabase
          .from("tournaments")
          .insert({
            name: formData.name.trim(),
            event_id: formData.event_id,
            age_group_id: isLegacyAgeGroup ? formData.age_group_id : null,
            division_name: divisionName,
            tournament_type: formData.tournament_type,
            max_teams: formData.max_teams,
            seeding_type: formData.seeding_type,
            status: "setup",
          })
          .select()
          .single();

        if (tournamentError) throw tournamentError;
        console.log("✅ Tournament created:", tournament);

        // Import teams from coach_teams table using division name
        console.log("🔄 Starting team import process...");
        await importTeamsFromEvent(tournament.id, formData.event_id, divisionName);
      }
      
      onSuccess();
      
      // Reset form
      setFormData({
        name: "",
        event_id: "",
        age_group_id: "",
        tournament_type: "single_elimination",
        max_teams: 32,
        seeding_type: "manual",
      });
    } catch (error: any) {
      console.error("Error with tournament:", error);
      toast.error(editTournament ? "Failed to update tournament" : "Failed to create tournament");
    } finally {
      setIsSubmitting(false);
    }
  };

  const importTeamsFromEvent = async (tournamentId: string, eventId: string, divisionName: string) => {
    console.log("🏆 Starting team import for tournament:", { tournamentId, eventId, divisionName });
    
    try {
      if (!divisionName) {
        console.warn("⚠️ No division name provided for team import");
        toast.info("No division selected. You can add teams manually later.");
        return;
      }

      // Get teams from coach_teams that match the division name
      console.log("🔍 Searching for teams with age_group:", divisionName);
      const { data: teams, error: teamsError } = await supabase
        .from("coach_teams")
        .select("id, name, age_group, head_coach_name")
        .eq("age_group", divisionName)
        .order("name")
        .limit(formData.max_teams);

      if (teamsError) {
        console.error("❌ Error fetching teams:", teamsError);
        toast.error("Failed to fetch teams");
        return;
      }

      console.log("📊 Teams found:", teams);
      console.log("📈 Team count:", teams?.length || 0);

      if (!teams || teams.length === 0) {
        console.warn("⚠️ No teams found for division:", divisionName);
        
        // Let's also check what teams exist in the database
        const { data: allTeams } = await supabase
          .from("coach_teams")
          .select("id, name, age_group");
        
        console.log("🔍 All teams in database:", allTeams);
        console.log("🔍 Available age groups:", [...new Set(allTeams?.map(t => t.age_group) || [])]);
        
        toast.info("No teams found for this division. You can add teams manually later.");
        return;
      }

      // Clear any existing teams for this tournament first
      console.log("🧹 Clearing existing tournament brackets...");
      const { error: deleteError } = await supabase
        .from("tournament_brackets")
        .delete()
        .eq("tournament_id", tournamentId);

      if (deleteError) {
        console.warn("⚠️ Warning: Could not clear existing brackets:", deleteError);
      }

      // Implement seeding logic based on seeding_type
      console.log("📝 Creating bracket entries for", teams.length, "teams with seeding type:", formData.seeding_type);
      
      let seedAssignments;
      
      switch (formData.seeding_type) {
        case "random":
          // Random seeding - shuffle seed numbers
          const seedNumbers = Array.from({ length: teams.length }, (_, i) => i + 1);
          for (let i = seedNumbers.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [seedNumbers[i], seedNumbers[j]] = [seedNumbers[j], seedNumbers[i]];
          }
          seedAssignments = seedNumbers;
          console.log("🎲 Random seeding applied:", seedAssignments);
          break;
          
        case "ranking_based":
          // TODO: Implement ranking-based seeding (for now, use alphabetical)
          seedAssignments = teams.map((_, index) => index + 1);
          console.log("🏆 Ranking-based seeding (placeholder - alphabetical order):", seedAssignments);
          break;
          
        case "manual":
        default:
          // Manual/default - alphabetical order (admin can adjust later)
          seedAssignments = teams.map((_, index) => index + 1);
          console.log("✏️ Manual seeding (alphabetical order):", seedAssignments);
          break;
      }

      const bracketEntries = teams.map((team, index) => ({
        tournament_id: tournamentId,
        team_id: team.id,
        team_name: team.name,
        coach_name: team.head_coach_name,
        seed_number: seedAssignments[index],
        bracket_position: seedAssignments[index],
      }));

      console.log("📋 Bracket entries to insert:", bracketEntries);

      const { data: insertedBrackets, error: bracketError } = await supabase
        .from("tournament_brackets")
        .insert(bracketEntries)
        .select();

      if (bracketError) {
        console.error("❌ Error inserting teams into brackets:", bracketError);
        toast.error("Failed to import teams into tournament");
        return;
      }

      console.log("✅ Teams successfully imported into tournament brackets:", insertedBrackets);
      console.log(`✅ Import complete: ${teams.length} teams added to tournament ${tournamentId}`);
      
      // Show specific success message
      if (teams.length === 1) {
        toast.success(`Tournament created with 1 team imported`);
      } else {
        toast.success(`Tournament created with ${teams.length} teams imported`);
      }
    } catch (error: any) {
      console.error("❌ Error importing teams:", error);
      toast.error("Failed to import teams into tournament");
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editTournament ? "Edit Tournament" : "Create Tournament"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Tournament Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData(prev => ({ ...prev, name: e.target.value }))
              }
              placeholder="Enter tournament name"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="event">Event</Label>
            <Select
              value={formData.event_id}
              onValueChange={(value) =>
                setFormData(prev => ({ ...prev, event_id: value }))
              }
              disabled={isLoading || isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((event) => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="age-group">Age Group</Label>
            <Select
              value={formData.age_group_id}
              onValueChange={(value) =>
                setFormData(prev => ({ ...prev, age_group_id: value }))
              }
              disabled={!formData.event_id || isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an age group" />
              </SelectTrigger>
              <SelectContent>
                {ageGroups.map((ageGroup) => (
                  <SelectItem key={ageGroup.id} value={ageGroup.id}>
                    {ageGroup.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label>Tournament Type</Label>
            <RadioGroup
              value={formData.tournament_type}
              onValueChange={(value) =>
                setFormData(prev => ({ ...prev, tournament_type: value }))
              }
              disabled={isSubmitting}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single_elimination" id="single-elim" />
                <Label htmlFor="single-elim">Single Elimination</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="double_elimination" id="double-elim" />
                <Label htmlFor="double-elim">Double Elimination</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="round_robin" id="round-robin" />
                <Label htmlFor="round-robin">Round Robin</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pool_play_playoffs" id="pool-playoffs" />
                <Label htmlFor="pool-playoffs">Pool Play + Playoffs</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max-teams">Maximum Teams</Label>
            <Input
              id="max-teams"
              type="number"
              min="4"
              max="128"
              value={formData.max_teams}
              onChange={(e) =>
                setFormData(prev => ({ ...prev, max_teams: parseInt(e.target.value) || 32 }))
              }
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="seeding">Seeding Type</Label>
            <Select
              value={formData.seeding_type}
              onValueChange={(value) =>
                setFormData(prev => ({ ...prev, seeding_type: value }))
              }
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="random">Random</SelectItem>
                <SelectItem value="ranking_based">Ranking-based</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editTournament ? "Update Tournament" : "Create Tournament"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};